import React, { useContext, useEffect, useState } from "react";
import { Button, Card, Col, Input, Row, Typography, message, Spin } from "antd";
import { ContextAPI } from "../../../ContextAPI";
import { platformApi } from "../../../api";
import VehicleName from "../../VehicleName";

const { Title, Text } = Typography;

const GatePassVerification = () => {
  const { loginCredintials } = useContext(ContextAPI);
  const [createAccess, setCreateAccess] = useState(false);
  const [otp, setOtp] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [record, setRecord] = useState(null);

  useEffect(() => {
    loginCredintials?.roleAccess?.forEach((each) => {
      if (each.subModule === "gate_pass_verification" && each.access?.create) {
        setCreateAccess(true);
      }
    });
  }, [loginCredintials]);

  const getPayload = (res) => res?.data?.response || res?.data;

  const isApiOk = (res) => {
    const payload = getPayload(res);
    return (
      (res?.status === 200 || res?.data?.code === 200) &&
      (payload?.code === 200 || payload?.code == null)
    );
  };

  const handleLookup = async () => {
    const code = otp.trim();
    if (!code) {
      message.error("Enter OTP");
      return;
    }
    setLookingUp(true);
    setRecord(null);
    try {
      const res = await platformApi.post("/api/jobOrder/gatePass/lookupOtp", {
        otp: code,
      });
      const payload = getPayload(res);
      if (!isApiOk(res) || !payload?.data) {
        message.error(payload?.message || payload?.msg || "Invalid or expired OTP");
        return;
      }
      setRecord(payload.data);
    } catch (err) {
      console.error("Gate Pass lookup error:", err);
      message.error(
        err?.response?.data?.response?.message ||
          err?.response?.data?.msg ||
          "Failed to verify OTP"
      );
    } finally {
      setLookingUp(false);
    }
  };

  const handleConfirm = async () => {
    if (!record?.jobOrderId || !otp.trim() || confirming || !createAccess) return;
    setConfirming(true);
    try {
      const res = await platformApi.post("/api/jobOrder/gatePass/confirm", {
        otp: otp.trim(),
        jobOrderId: record.jobOrderId,
      });
      const payload = getPayload(res);
      if (!isApiOk(res)) {
        message.error(payload?.message || payload?.msg || "Failed to confirm gate pass");
        return;
      }
      message.success("Gate Pass verified! Record moved to Feedback Call.");
      setOtp("");
      setRecord(null);
    } catch (err) {
      console.error("Gate Pass confirm error:", err);
      message.error(
        err?.response?.data?.response?.message ||
          err?.response?.data?.msg ||
          "Failed to confirm gate pass"
      );
    } finally {
      setConfirming(false);
    }
  };

  const InfoRow = ({ label, value }) => (
    <Row style={{ marginBottom: 8 }}>
      <Col span={10}>
        <Text type="secondary">{label}</Text>
      </Col>
      <Col span={14}>
        <Text strong>{value || "-"}</Text>
      </Col>
    </Row>
  );

  return (
    <div style={{ padding: "16px 20px" }}>
      <Title level={3} style={{ marginBottom: 4 }}>
        Gate Pass Verification
      </Title>
      <Text type="secondary">Security gate exit — verify customer OTP</Text>

      <Card style={{ marginTop: 20, maxWidth: 560 }}>
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          OTP
        </Text>
        <Row gutter={12} type="flex" align="middle">
          <Col span={16}>
            <Input
              placeholder="Enter customer OTP"
              value={otp}
              maxLength={8}
              style={{ height: 40, fontSize: 16, fontWeight: 600, letterSpacing: 3 }}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              onPressEnter={handleLookup}
            />
          </Col>
          <Col span={8}>
            <Button
              type="primary"
              loading={lookingUp}
              disabled={!otp.trim()}
              onClick={handleLookup}
              style={{
                height: 40,
                width: "100%",
                fontWeight: 600,
                backgroundColor: otp.trim() ? "#0d9488" : undefined,
                borderColor: otp.trim() ? "#0d9488" : undefined,
              }}
            >
              Verify
            </Button>
          </Col>
        </Row>
      </Card>

      <Spin spinning={lookingUp}>
        {record ? (
          <Card
            title="Record Card"
            style={{ marginTop: 16, maxWidth: 560 }}
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                Job No: {record.jobNo || "-"}
              </Text>
            }
          >
            {record.vehicleImage ? (
              <div style={{ marginBottom: 16, textAlign: "center" }}>
                <img
                  src={record.vehicleImage}
                  alt="Vehicle"
                  style={{ maxHeight: 120, maxWidth: "100%", objectFit: "contain" }}
                />
              </div>
            ) : null}
            <InfoRow
              label="Model"
              value={<VehicleName nameOnly modelName={record.modelName} />}
            />
            <InfoRow label="Customer" value={record.customerName} />
            <InfoRow label="Mobile" value={record.customerPhone} />
            <InfoRow label="Register No" value={record.registerNo} />
            <InfoRow label="Chassis No" value={record.chassisNo} />
            <InfoRow
              label="Odometer"
              value={record.odometer != null ? `${record.odometer} km` : null}
            />
            <InfoRow label="Service Type" value={record.serviceType} />
            <InfoRow label="Status" value={record.jobStatus} />

            <Button
              type="primary"
              size="large"
              loading={confirming}
              disabled={!createAccess || confirming}
              onClick={handleConfirm}
              style={{
                marginTop: 16,
                width: "100%",
                fontWeight: 600,
                backgroundColor: createAccess ? "#d97706" : undefined,
                borderColor: createAccess ? "#d97706" : undefined,
              }}
            >
              {createAccess ? "Mark Verified" : "No permission to verify"}
            </Button>
          </Card>
        ) : (
          <Card style={{ marginTop: 16, maxWidth: 560, textAlign: "center" }}>
            <Text type="secondary">
              Enter OTP and tap Verify to load the vehicle record
            </Text>
          </Card>
        )}
      </Spin>
    </div>
  );
};

export default GatePassVerification;
